import { Box, ButtonV2, Flex, Heading, Text } from '@ghq-abi/design-system';
import { useTranslate } from '@tolgee/react';
import { useRouter } from 'next/router';
import { useCallback, useState } from 'react';
import { Ticket } from '~/entities/MyTickets/types';
import { ArrowExpand } from '~/shared/components/icons';
import { GRID_CONSTANTS } from '~/shared/constants';

type GridCardProps = {
  ticket: Ticket;
};

export function GridCard({ ticket }: GridCardProps) {
  const { t } = useTranslate();
  const router = useRouter();
  const [hovered, setHovered] = useState(false);

  const {
    id,
    title,
    status,
    rawStatus,
    ticketNumber,
    businessUnit,
    zone,
    category,
    subcategory,
    employee,
    createDate,
  } = ticket;

  const handleMouseEnter = useCallback(() => setHovered(true), []);
  const handleMouseLeave = useCallback(() => setHovered(false), []);
  const handleViewDetails = useCallback(() => {
    router.push(`/tickets/my-tickets/${id}`);
  }, [router, id]);

  return (
    <Box
      css={{
        position: 'relative',
        width: '100%',
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Box
        css={{
          position: hovered ? 'absolute' : 'relative',
          zIndex: hovered ? 1000 : 1,
          top: 0,
          left: 0,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '$white',
          border: `1px solid ${GRID_CONSTANTS.BORDER_COLOR}`,
          borderRadius: GRID_CONSTANTS.BORDER_RADIUS,
          padding: '$4',
          boxShadow: hovered
            ? GRID_CONSTANTS.SHADOW_GRID_HOVER
            : GRID_CONSTANTS.SHADOW_NORMAL,
          width: hovered ? GRID_CONSTANTS.HOVER_WIDTH : '100%',
          transform: hovered ? GRID_CONSTANTS.HOVER_TRANSFORM : 'translateY(0)',
          transition: `all ${GRID_CONSTANTS.TRANSITION_DURATION} ease`,
          minWidth: hovered ? GRID_CONSTANTS.HOVER_WIDTH : 'auto',
        }}
      >
        <Flex justify="between">
          <Flex direction="column" css={{ flex: 1, minWidth: 0 }}>
            <Heading
              as="strong"
              size="sm"
              css={{
                fontSize: '$sm',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                marginBottom: '$2',
              }}
            >
              {t(title)} - {category ? t(category) : 'N/A'}
            </Heading>

            <Text css={{ fontSize: '$xs' }}>
              {t(`my_tickets.status.${status}`)}
            </Text>
          </Flex>

          <ButtonV2
            variant="secondary"
            size="md"
            onClick={handleViewDetails}
            css={{
              padding: '$1 $2',
              boxShadow: 'none',
              border: 'none',
              color: '$black',
            }}
          >
            <ArrowExpand />
          </ButtonV2>
        </Flex>

        {hovered && (
          <Box
            css={{
              marginTop: '$4',
              opacity: hovered ? 1 : 0,
              transition: `all ${GRID_CONSTANTS.TRANSITION_DURATION} ease`,
            }}
          >
            <Box
              css={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '$3',
                padding: '$3',
                backgroundColor: '#F8F9FA',
                borderRadius: '6px',
                fontSize: '$xs',
              }}
            >
              <Text><strong>{t('myTickets.ticketId')}:</strong> {ticketNumber}</Text>
              <Text><strong>{t('myTickets.subcategory')}:</strong> {subcategory ? t(subcategory) : '—'}</Text>
              <Text><strong>{t('myTickets.employee')}:</strong> {employee.name}</Text>
              <Text><strong>{t('myTickets.bu')}:</strong> {businessUnit}</Text>
              <Text><strong>{t('myTickets.zone')}:</strong> {zone}</Text>
              <Text><strong>{t('myTickets.createdDate')}:</strong> {createDate}</Text>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
